import { Suspense } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { PageLoader } from '@/components/PageLoader';
import { RouteErrorBoundary } from '@/components/AppErrorBoundary';
import { useState, useEffect } from 'react';
import { LayoutDashboard, Rocket, Users, MessageSquare, MessagesSquare, BookOpen, Calendar, CalendarDays, Sparkles, NotebookPen, Trophy, Search, Plus, Building2, Heart, Briefcase, GraduationCap, LogOut, Store, Coins, FolderLock, Landmark, Home, Network, Menu, UserPlus, Compass, Wallet, ClipboardCheck, ClipboardList, BarChart3, CalendarClock, Radar, BadgeCheck, TreePine, Send } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { ThemeToggle } from '@/components/brand/ThemeToggle';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import MessagesBell from '@/components/MessagesBell';
// MessagingProvider moved up to App.js so it survives AppShell unmounts.
import { LiveNowChip } from '@/components/layout/LiveNowChip';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';
import { recordModuleVisit } from '@/hooks/use-modules-explored';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/use-auth';
import { usePlatform } from '@/hooks/use-platform';
import { useAccess } from '@/hooks/use-access';
import { useVenture, useFounderScore } from '@/hooks/use-founder';
import { useMentorAccess } from '@/hooks/use-mentor';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';
import AutoPushPrompt from '@/components/AutoPushPrompt';

// Mentor portal nav — EVERY mentor gets ALL portal modules by default.
// "sessions" and "checkin" merged into a single "Sessions" module: /app/mentor-checkin
// hosts the classes & 1:1s list, weekly availability, and 1:1 booking requests.
// LMS ("Learning Materials") stays inside the mentor shell via /app/mentor-lms so
// the sidebar never disappears.
const MENTOR_NAV = {
  home: { to: '/app/mentor-home', label: 'Mentor Home', icon: LayoutDashboard, end: true },
  mentees: { to: '/app/mentor-mentees', label: 'My Mentees', icon: Users },
  checkin: { to: '/app/mentor-checkin', label: 'Sessions', icon: CalendarClock },
  attendance: { to: '/app/mentor-attendance', label: 'Attendance', icon: ClipboardCheck },
  submissions: { to: '/app/mentor-submissions', label: 'Submissions', icon: ClipboardList },
  analytics: { to: '/app/mentor-analytics', label: 'Analytics', icon: BarChart3 },
  lms: { to: '/app/mentor-lms', label: 'Learning Materials', icon: BookOpen },
  messages: { to: '/app/messages', label: 'Messages', icon: MessagesSquare },
  finance: { to: '/app/mentor-finance', label: 'Finance', icon: Wallet },
  community: { to: '/app/community', label: 'Community', icon: MessageSquare },
};

const FEATURE_PATHS = [
  { prefix: '/app/copilot', key: 'ai_copilot' },
  { prefix: '/app/mentors', key: 'mentors' },
  { prefix: '/app/community', key: 'community' },
  { prefix: '/app/events', key: 'events' },
  { prefix: '/app/investors', key: 'investors' },
  { prefix: '/app/internships', key: 'career' },
];

// App-access (user-type) route gating — mirrors the RBAC App Access matrix.
const APP_PATHS = [
  { prefix: '/app/venture', key: 'venture' },
  { prefix: '/app/copilot', key: 'copilot' },
  { prefix: '/app/learn', key: 'learning' },
  { prefix: '/app/journal', key: 'journal' },
  { prefix: '/app/canopy', key: 'score' },
  { prefix: '/app/score', key: 'score' },
  { prefix: '/app/mentors', key: 'mentors' },
  { prefix: '/app/community', key: 'community' },
  { prefix: '/app/events', key: 'events' },
  { prefix: '/app/network', key: 'network' },
  { prefix: '/app/investors', key: 'investors' },
  { prefix: '/app/match', key: 'match' },
  { prefix: '/app/crm', key: 'crm' },
  { prefix: '/app/internships', key: 'career' },
];

const navWorkspace = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/app/venture', label: 'Venture', icon: Rocket, appKey: 'venture' },
  { to: '/app/copilot', label: 'AI Copilot', icon: Sparkles, accent: true, featureKey: 'ai_copilot', appKey: 'copilot' },
  { to: '/app/learn', label: 'Learning', icon: BookOpen, appKey: 'learning' },
  { to: '/app/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/app/vault', label: 'Founder Vault', icon: FolderLock },
  { to: '/app/journal', label: 'Journal', icon: NotebookPen, appKey: 'journal' },
  { to: '/app/canopy', label: 'Canopy', icon: TreePine, appKey: 'score' },
];

const navNetwork = [
  { to: '/app/events', label: 'Events', icon: Calendar, featureKey: 'events', appKey: 'events' },
  { to: '/app/community', label: 'Community', icon: MessageSquare, featureKey: 'community', appKey: 'community' },
  { to: '/app/messages', label: 'Messages', icon: Send },
  { to: '/app/mentors', label: 'Mentors', icon: Users, featureKey: 'mentors', appKey: 'mentors' },
  { to: '/app/network', label: 'My Network', icon: UserPlus, appKey: 'network' },
  { to: '/app/match', label: 'Cofounder Match', icon: Heart, appKey: 'match' },
  { to: '/app/alumni', label: 'Alumni Network', icon: Network },
];

const navGrowth = [
  { to: '/app/discover', label: 'Opportunities', icon: Radar },
  { to: '/app/marketplace', label: 'Marketplace', icon: Store },
  { to: '/app/ventures/explore', label: 'Explore Ventures', icon: Compass },
  { to: '/app/investors', label: 'Investor Connect', icon: Building2, featureKey: 'investors', appKey: 'investors' },
  { to: '/app/crm', label: 'Startup CRM', icon: Briefcase, appKey: 'crm' },
  { to: '/app/internships', label: 'Jobs & Internships', icon: GraduationCap, featureKey: 'career', appKey: 'career' },
  { to: '/app/capital', label: 'Bower Capital', icon: Landmark },
];

const PERSONA_HOME = {
  'student-build': { route: '/app',              label: 'Dashboard' },
  'student-seed':  { route: '/app',              label: 'Dashboard' },
  'mentor':        { route: '/app/mentor-home',  label: 'Mentor home' },
  'parent':        { route: '/app/parent',       label: 'Parent home' },
  'admin':         { route: '/app/admin-home',   label: 'Admin home' },
  'investor':      { route: '/app/investor-home',label: 'Investor home' },
  'alumni':        { route: '/app/alumni-home',  label: 'Alumni home' },
  'partner':       { route: '/app/partner-home', label: 'Partner home' },
  'user':          { route: '/app/user-home',    label: 'Home' },
};

// Focused console navs for non-student / non-mentor personas. Each entry links to
// an existing page so there are no dead routes.
const PERSONA_NAV = {
  investor: [
    { to: '/app/investor-home', label: 'Investor Home', icon: LayoutDashboard, end: true },
    { to: '/app/investors', label: 'Deal Flow', icon: Building2 },
    { to: '/app/ventures/explore', label: 'Explore Ventures', icon: Compass },
    { to: '/app/events', label: 'Events', icon: Calendar },
    { to: '/app/community', label: 'Community', icon: MessageSquare },
  ],
  parent: [
    { to: '/app/parent', label: 'Parent Home', icon: LayoutDashboard, end: true },
    { to: '/app/events', label: 'Events', icon: Calendar },
    { to: '/app/community', label: 'Community', icon: MessageSquare },
  ],
  alumni: {
    // Alumni get the FULL member workspace (same modules as students) with
    // Alumni Home as their landing page. isStaff bypasses feature/app gates.
    workspace: [
      { to: '/app/alumni-home', label: 'Alumni Home', icon: LayoutDashboard, end: true },
      { to: '/app/venture', label: 'Venture', icon: Rocket },
      { to: '/app/copilot', label: 'AI Copilot', icon: Sparkles, accent: true },
      { to: '/app/learn', label: 'Learning', icon: BookOpen },
      { to: '/app/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/app/vault', label: 'Founder Vault', icon: FolderLock },
      { to: '/app/journal', label: 'Journal', icon: NotebookPen },
      { to: '/app/canopy', label: 'Canopy', icon: TreePine },
    ],
    network: [
      { to: '/app/events', label: 'Events', icon: Calendar },
      { to: '/app/community', label: 'Community', icon: MessageSquare },
      { to: '/app/messages', label: 'Messages', icon: Send },
      { to: '/app/mentors', label: 'Mentors', icon: Users },
      { to: '/app/network', label: 'My Network', icon: UserPlus },
      { to: '/app/match', label: 'Cofounder Match', icon: Heart },
      { to: '/app/alumni', label: 'Alumni Network', icon: Network },
    ],
    growth: [
      { to: '/app/discover', label: 'Opportunities', icon: Radar },
      { to: '/app/marketplace', label: 'Marketplace', icon: Store },
      { to: '/app/ventures/explore', label: 'Explore Ventures', icon: Compass },
      { to: '/app/investors', label: 'Investor Connect', icon: Building2 },
      { to: '/app/crm', label: 'Startup CRM', icon: Briefcase },
      { to: '/app/internships', label: 'Jobs & Internships', icon: GraduationCap },
      { to: '/app/capital', label: 'Bower Capital', icon: Landmark },
    ],
  },
  partner: [
    { to: '/app/partner-home', label: 'Partner Home', icon: LayoutDashboard, end: true },
    { to: '/app/ventures/explore', label: 'Explore Ventures', icon: Compass },
    { to: '/app/internships', label: 'Post a Role', icon: GraduationCap },
    { to: '/app/events', label: 'Events', icon: Calendar },
    { to: '/app/community', label: 'Community', icon: MessageSquare },
  ],
  user: {
    // `user` persona (pre-cohort) now sees the FULL navigation grouped into
    // Workspace / Network / Growth so it mirrors the student home structure.
    // Access-level restrictions are enforced per-page (locked CTAs on Mentors
    // booking / Investor Connect / Internships, 5-question/month cap on AI
    // Copilot, free-tier only on Learning). No new routes — everything below
    // is a route that already exists.
    workspace: [
      { to: '/app/user-home', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/app/venture', label: 'Venture', icon: Rocket },
      { to: '/app/copilot', label: 'AI Copilot', icon: Sparkles, accent: true },
      { to: '/app/learn', label: 'Learning', icon: BookOpen },
      { to: '/app/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/app/vault', label: 'Founder Vault', icon: FolderLock },
      { to: '/app/journal', label: 'Journal', icon: NotebookPen },
      { to: '/app/canopy', label: 'Canopy', icon: TreePine },
    ],
    network: [
      { to: '/app/events', label: 'Events', icon: Calendar },
      { to: '/app/community', label: 'Community', icon: MessageSquare },
      { to: '/app/messages', label: 'Messages', icon: Send },
      { to: '/app/mentors', label: 'Mentors', icon: Users },
      { to: '/app/network', label: 'My Network', icon: UserPlus },
      { to: '/app/match', label: 'Cofounder Match', icon: Heart },
      { to: '/app/alumni', label: 'Alumni Network', icon: Network },
    ],
    growth: [
      { to: '/app/discover', label: 'Opportunities', icon: Radar },
      { to: '/app/marketplace', label: 'Marketplace', icon: Store },
      { to: '/app/ventures/explore', label: 'Explore Ventures', icon: Compass },
      { to: '/app/investors', label: 'Investor Connect', icon: Building2 },
      { to: '/app/crm', label: 'Startup CRM', icon: Briefcase },
      { to: '/app/internships', label: 'Jobs & Internships', icon: GraduationCap },
      { to: '/app/capital', label: 'Bower Capital', icon: Landmark },
    ],
    account: [
      { to: '/app/settings', label: 'Settings', icon: Compass },
    ],
  },
};

const STUDENT_PERSONAS = new Set(['student-build', 'student-seed']);

// Hoisted so React keeps the same component identity across AppShell re-renders —
// defining it inline remounted the whole nav on every route change, snapping the
// sidebar scroll position back to the top.
const NavList = ({ items, label }) => (
  <div>
    <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-grotesk">{label}</p>
    <ul className="space-y-0.5">
      {items.map((n) => (
        <li key={n.to}>
          <NavLink
            to={n.to}
            end={n.end}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
              isActive ? 'bg-foreground text-background' : 'text-foreground/70 hover:text-foreground hover:bg-secondary'
            )}
          >
            <n.icon className="h-4 w-4" />
            <span>{n.label}</span>
            {n.accent && <span className="ml-auto ai-chip !text-[9px] !px-1.5 !py-0.5">AI</span>}
          </NavLink>
        </li>
      ))}
    </ul>
  </div>
);

const AppShell = () => {
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user: authUser, logout, impersonation } = useAuth();
  const { feature, maintenance, maintenanceMessage, productName } = usePlatform();
  const { appAllowed } = useAccess();
  const { venture } = useVenture();
  const { score } = useFounderScore();
  const currentUser = authUser || {};
  const persona = authUser?.persona;
  const home = PERSONA_HOME[persona] || PERSONA_HOME['student-build'];
  const isStudent = !persona || STUDENT_PERSONAS.has(persona);
  const isMentor = persona === 'mentor';
  const personaNav = PERSONA_NAV[persona] || null;
  // `user` persona uses a grouped nav (workspace/network/growth/account).
  // Other personas still use a flat array. Flatten the grouped structure
  // when we need a single list (e.g. mobile bottom bar first 4 items).
  const personaNavIsGrouped = personaNav && !Array.isArray(personaNav);
  const personaNavFlat = personaNavIsGrouped
    ? [...(personaNav.workspace || []), ...(personaNav.network || []), ...(personaNav.growth || []), ...(personaNav.account || [])]
    : (personaNav || []);
  const isStaff = ['admin', 'mentor', 'investor', 'parent', 'alumni', 'partner'].includes(persona);
  // Pre-cohort `user` persona sees every module in the sidebar. Per-page
  // locked CTAs (Investor Connect / Internships / Mentors booking / Copilot
  // cap) handle the actual restrictions — org-level `feature()` toggles and
  // the `appAllowed()` matrix would otherwise "turn off" whole surfaces
  // and defeat the "taste the app then apply" flow. Bypass both for `user`.
  const bypassGates = isStaff || persona === 'user';
  const { modules: mentorModules } = useMentorAccess();
  // Every mentor gets the FULL portal navigation (all 9 modules). Fine-grained
  // scoping now happens on CONTENT (which cohorts/courses/modules they see
  // inside each portal module) — not on which nav items appear.
  const MENTOR_NAV_ORDER = ['home', 'mentees', 'checkin', 'messages', 'community', 'submissions', 'attendance', 'lms', 'analytics', 'finance'];
  const mentorNav = MENTOR_NAV_ORDER.map((k) => MENTOR_NAV[k]).filter(Boolean);
  // `mentorModules` is still fetched (used by other screens that check
  // capabilities) — nav no longer filters on it.
  void mentorModules;

  const gate = (items) => items.filter((n) => (!n.featureKey || feature(n.featureKey)) && appAllowed(n.appKey));

  // Workspace nav adapts the first item to the persona's home + respects feature flags.
  const navWorkspaceDynamic = [
    { to: home.route, label: home.label, icon: LayoutDashboard, end: true },
    ...gate(navWorkspace.slice(1)),
  ];
  const navGrowthGated = gate(navGrowth);
  const navNetworkGated = gate(navNetwork);

  // A disabled feature's page is blocked for students (staff/admins/pre-cohort users keep access).
  const featureBlocked = !bypassGates && FEATURE_PATHS.find((f) => pathname.startsWith(f.prefix) && !feature(f.key));
  // A user-type without app access to a module is blocked (admins + pre-cohort users always pass).
  const appBlocked = !bypassGates && APP_PATHS.find((f) => pathname.startsWith(f.prefix) && !appAllowed(f.key));
  const blocked = featureBlocked || appBlocked;

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  // Track modules explored for the pre-cohort user nudge banner.
  useEffect(() => {
    if (persona === 'user') recordModuleVisit(pathname);
  }, [pathname, persona]);

  const mobileNav = isMentor ? [
    { to: '/app/mentor-home', label: 'Home', icon: LayoutDashboard, end: true },
    { to: '/app/mentor-checkin', label: 'Sessions', icon: CalendarClock },
    { to: '/app/mentor-mentees', label: 'Mentees', icon: Users },
    { to: '/app/community', label: 'B-Cafe', icon: MessageSquare },
  ] : personaNav ? personaNavFlat.slice(0, 4).map((n) => ({ to: n.to, label: n.label.split(' ')[0], icon: n.icon, end: n.end })) : [
    { to: home.route, label: 'Home', icon: LayoutDashboard, end: true },
    { to: '/app/venture', label: 'Venture', icon: Rocket },
    { to: '/app/copilot', label: 'Copilot', icon: Sparkles },
    { to: '/app/community', label: 'B-Cafe', icon: MessageSquare },
  ];


  return (
    maintenance && !isStaff ? (
      <div className="min-h-screen grid place-items-center bg-background text-foreground px-6" data-testid="maintenance-screen">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6"><Logo /></div>
          <div className="h-14 w-14 rounded-2xl bg-amber/15 text-amber grid place-items-center mx-auto mb-5"><Sparkles className="h-6 w-6" /></div>
          <h1 className="font-display text-3xl sm:text-4xl">We’ll be right back.</h1>
          <p className="text-muted-foreground mt-3">{maintenanceMessage || `${productName} is undergoing a quick tune-up.`}</p>
          <Button variant="outline" className="mt-6 rounded-full" onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </div>
    ) : (
    <div className={cn('min-h-screen flex bg-background text-foreground', impersonation && 'pt-10')}>
      {/* Sidebar (desktop) */}
      <aside className={cn('hidden lg:flex w-[264px] shrink-0 border-r border-border flex-col bg-card/40 backdrop-blur sticky h-screen', impersonation ? 'top-10' : 'top-0')} data-testid="app-sidebar">
        <div className="px-5 pt-5 pb-3">
          <Link to="/app"><Logo /></Link>
        </div>
        <div className="px-3 mt-2">
          {isMentor ? (
            <Link to="/app/mentor-checkin">
              <Button className="w-full justify-start gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="sm">
                <CalendarClock className="h-4 w-4" /> My sessions
              </Button>
            </Link>
          ) : personaNav && persona !== 'user' && persona !== 'alumni' ? (
            <Link to={home.route}>
              <Button className="w-full justify-start gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="sm">
                <LayoutDashboard className="h-4 w-4" /> {home.label}
              </Button>
            </Link>
          ) : null}
        </div>
        <nav className="px-3 mt-4 flex-1 overflow-y-auto scrollbar-hide space-y-6">
          {isMentor ? (
            <NavList items={mentorNav} label="Mentor" />
          ) : personaNavIsGrouped ? (
            <>
              <NavList items={personaNav.workspace || []} label="Workspace" />
              {personaNav.network?.length > 0 && <NavList items={personaNav.network} label="Network" />}
              {personaNav.growth?.length > 0 && <NavList items={personaNav.growth} label="Growth" />}
              {personaNav.account?.length > 0 && <NavList items={personaNav.account} label="Account" />}
            </>
          ) : personaNav ? (
            <NavList items={personaNav} label="Workspace" />
          ) : (
            <>
              <NavList items={navWorkspaceDynamic} label="Workspace" />
              <NavList items={navNetworkGated} label="Network" />
              <NavList items={navGrowthGated} label="Growth" />
            </>
          )}

          {isStudent && venture && (
            <div>
              <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-grotesk">Pinned Venture</p>
              <Link to="/app/venture" className="block mx-1 rounded-xl border border-border bg-card p-3 hover:bg-secondary transition-colors" data-testid="sidebar-pinned-venture">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-sm">{venture.name[0]}</div>
                  <div className="text-sm font-medium truncate">{venture.name}</div>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-grotesk">Stage · {venture.stage}</div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${venture.progress}%` }} />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">{venture.progress}% — to Launch</div>
              </Link>
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-border">
          <Link to="/app/settings" className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary transition-colors group" data-testid="sidebar-profile-link">
            <Avatar className="h-9 w-9">
              <AvatarImage src={currentUser.avatar} alt={currentUser.name} />
              <AvatarFallback>{(currentUser.first_name || currentUser.name || "U")[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate flex items-center gap-1">
                {currentUser.name}
                {currentUser.verified && <BadgeCheck className="h-3 w-3 text-primary shrink-0" />}
              </p>
              {currentUser.bio_oneliner && <p className="text-[11px] text-muted-foreground truncate">{currentUser.bio_oneliner}</p>}
              <p className="text-[10px] text-muted-foreground truncate font-mono">UID · {currentUser.student_id || (currentUser.programs && currentUser.programs[0]) || 'Bower'}</p>
            </div>
            {isStudent && (
              <div className="ml-auto rounded-md bg-primary/10 text-primary text-[10px] font-grotesk px-1.5 py-1 border border-primary/30 font-semibold">
                {score}
              </div>
            )}
          </Link>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        {/* Topbar */}
        <header className={cn('sticky z-30 bg-background/85 backdrop-blur border-b border-border pt-safe', impersonation ? 'top-10' : 'top-0')}>
          <div className="flex items-center gap-2 px-4 lg:px-8 py-3">
            {/* Mobile: hamburger */}
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden h-9 w-9 shrink-0 -ml-1"
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[300px] bg-card border-r border-border overflow-y-auto" data-testid="mobile-drawer">
                <SheetHeader className="px-5 pt-5 pb-3 text-left">
                  <SheetTitle asChild>
                    <Link to="/app" className="inline-flex" onClick={() => setDrawerOpen(false)}><Logo /></Link>
                  </SheetTitle>
                </SheetHeader>
                <div className="px-3">
                  {isMentor ? (
                    <Link to="/app/mentor-checkin" onClick={() => setDrawerOpen(false)}>
                      <Button className="w-full justify-start gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="sm">
                        <CalendarClock className="h-4 w-4" /> My sessions
                      </Button>
                    </Link>
                  ) : personaNav && persona !== 'user' && persona !== 'alumni' ? (
                    <Link to={home.route} onClick={() => setDrawerOpen(false)}>
                      <Button className="w-full justify-start gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="sm">
                        <LayoutDashboard className="h-4 w-4" /> {home.label}
                      </Button>
                    </Link>
                  ) : null}
                </div>
                <nav className="px-3 mt-5 pb-6 space-y-6">
                  {isMentor ? (
                    <NavList items={mentorNav} label="Mentor" />
                  ) : personaNavIsGrouped ? (
                    <>
                      <NavList items={personaNav.workspace || []} label="Workspace" />
                      {personaNav.network?.length > 0 && <NavList items={personaNav.network} label="Network" />}
                      {personaNav.growth?.length > 0 && <NavList items={personaNav.growth} label="Growth" />}
                      {personaNav.account?.length > 0 && <NavList items={personaNav.account} label="Account" />}
                    </>
                  ) : personaNav ? (
                    <NavList items={personaNav} label="Workspace" />
                  ) : (
                    <>
                      <NavList items={navWorkspaceDynamic} label="Workspace" />
                      <NavList items={navNetworkGated} label="Network" />
                      <NavList items={navGrowthGated} label="Growth" />
                    </>
                  )}
                </nav>
                <div className="p-3 mt-auto border-t border-border bg-card sticky bottom-0">
                  <Link to="/app/settings" className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary transition-colors" onClick={() => setDrawerOpen(false)} data-testid="mobile-profile-link">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={currentUser.avatar} alt={currentUser.name} />
                      <AvatarFallback>{(currentUser.first_name || currentUser.name || "U")[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate flex items-center gap-1">
                        {currentUser.name}
                        {currentUser.verified && <BadgeCheck className="h-3 w-3 text-primary shrink-0" />}
                      </p>
                      {currentUser.bio_oneliner && <p className="text-[11px] text-muted-foreground truncate">{currentUser.bio_oneliner}</p>}
                      <p className="text-[10px] text-muted-foreground truncate font-mono">UID · {currentUser.student_id || (currentUser.programs && currentUser.programs[0]) || 'Bower'}</p>
                    </div>
                    {isStudent && (
                      <div className="rounded-md bg-primary/10 text-primary text-[10px] font-grotesk px-1.5 py-1 border border-primary/30 font-semibold">
                        {score}
                      </div>
                    )}
                  </Link>
                </div>
              </SheetContent>
            </Sheet>

            <div className="lg:hidden">
              <Link to="/app"><Logo mark /></Link>
            </div>
            <div className="relative flex-1 min-w-0 max-w-xl ml-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search…" className="pl-9 bg-card border-border h-10 rounded-lg" data-testid="topbar-search" />
              <kbd className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-grotesk text-muted-foreground border border-border rounded px-1.5 py-0.5">⌘ K</kbd>
            </div>
            <div className="ml-auto flex items-center gap-1.5 md:gap-2 shrink-0">
              <WorkspaceSwitcher />
              <LiveNowChip />
              <ThemeToggle />
              <MessagesBell />
              <NotificationsBell />
              <Link to="/signin" onClick={(e) => { e.preventDefault(); logout(); window.location.href = '/signin'; }} className="hidden md:inline-flex">
                <Button variant="ghost" size="icon" className="h-9 w-9" title="Sign out" data-testid="signout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {persona !== 'admin' && !impersonation && <OnboardingFlow />}
        {!impersonation && <AutoPushPrompt />}

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border pb-safe" data-testid="mobile-nav">
          <ul className="grid grid-cols-5">
            {mobileNav.map((n) => {
              const active = pathname === n.to || (n.to !== '/app' && pathname.startsWith(n.to));
              return (
                <li key={n.to}>
                  <NavLink to={n.to} end={n.end} className={cn('flex flex-col items-center gap-1 py-2.5 text-[10px] font-grotesk uppercase tracking-wider',
                    active ? 'text-primary' : 'text-muted-foreground')}>
                    <n.icon className="h-5 w-5" />
                    <span>{n.label}</span>
                  </NavLink>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                data-testid="mobile-nav-more"
                className={cn('w-full flex flex-col items-center gap-1 py-2.5 text-[10px] font-grotesk uppercase tracking-wider',
                  drawerOpen ? 'text-primary' : 'text-muted-foreground')}
              >
                <Menu className="h-5 w-5" />
                <span>More</span>
              </button>
            </li>
          </ul>
        </nav>

        <section className="flex-1 pb-24 lg:pb-10">
          {blocked ? (
            <div className="flex flex-col items-center justify-center text-center py-32 px-6" data-testid="feature-disabled">
              <div className="h-14 w-14 rounded-2xl bg-secondary text-muted-foreground grid place-items-center mb-5"><Sparkles className="h-6 w-6" /></div>
              <h2 className="font-display text-3xl">This module is turned off.</h2>
              <p className="text-muted-foreground mt-2 max-w-md text-sm">{blocked.key === 'ai_copilot' ? 'AI Copilot' : blocked.key} is currently disabled by your program team. Check back soon.</p>
              <Button asChild variant="outline" className="mt-6 rounded-full"><Link to="/app">Back to Dashboard</Link></Button>
            </div>
          ) : (
            <RouteErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </RouteErrorBoundary>
          )}
        </section>
      </main>
    </div>
    )
  );
};

export default AppShell;
